import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCanonical } from '@/hooks/useCanonical';
import SeoTitle from '@/components/SeoTitle';

const navLinks = [
  { label: 'Энциклопедия', target: 'encyclopedia' },
  { label: 'Продажи и переговоры', target: 'sales' },
];

const encyclopediaItems = [
  { label: 'Что такое корпоративное обучение', to: '/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie' },
  { label: 'Обучение персонала', to: '/enciklopediya-korporativnogo-obucheniya/obuchenie-personala' },
  { label: 'Обучение отдела продаж', to: '/enciklopediya-korporativnogo-obucheniya/obuchenie-otdela-prodazh' },
  { label: 'Тренинг для руководителей', to: '/enciklopediya-korporativnogo-obucheniya/trening-dlya-rukovoditeley' },
  { label: 'Soft Skills', to: '/enciklopediya-korporativnogo-obucheniya/razvitie-soft-skills' },
  { label: 'Командообразование', to: '/enciklopediya-korporativnogo-obucheniya/komandoobrazovanie' },
  { label: 'Адаптация сотрудников', to: '/enciklopediya-korporativnogo-obucheniya/adaptaciya-sotrudnikov' },
  { label: 'Корпоративная академия', to: '/enciklopediya-korporativnogo-obucheniya/korporativnaya-akademiya' },
  { label: 'Оценка эффективности обучения', to: '/enciklopediya-korporativnogo-obucheniya/ocenka-effektivnosti-obucheniya' },
  { label: 'Разработка программ обучения', to: '/enciklopediya-korporativnogo-obucheniya/razrabotka-programm-obucheniya' },
];

const salesItems = [
  { label: 'Тренинг по продажам', to: '/prodazhi-i-peregovory/trening-po-prodazham' },
  { label: 'Тренинг по переговорам', to: '/prodazhi-i-peregovory/trening-po-peregovoram' },
  { label: 'Работа с возражениями', to: '/prodazhi-i-peregovory/trening-po-rabote-s-vozrazheniyami' },
  { label: 'Холодные звонки', to: '/prodazhi-i-peregovory/trening-po-holodnym-zvonkam' },
  { label: 'Телефонные продажи', to: '/prodazhi-i-peregovory/trening-po-telefonnym-prodazham' },
  { label: 'Выявление потребностей', to: '/prodazhi-i-peregovory/trening-po-vyyavleniyu-potrebnostey' },
  { label: 'B2B продажи', to: '/prodazhi-i-peregovory/trening-po-prodazham-b2b' },
  { label: 'Работа с клиентами', to: '/prodazhi-i-peregovory/trening-po-rabote-s-klientami' },
  { label: 'Продажи через переписку', to: '/prodazhi-i-peregovory/trening-po-prodazham-cherez-perepisku' },
];

function PageMeta404() {
  return (
    <>
      <SeoTitle title="Страница не найдена — 404 | BusinessTraining.by" />
      <meta name="description" content="Запрашиваемая страница не найдена. Ознакомьтесь с корпоративными тренингами, обучением продажам, переговорам и другими программами BusinessTraining.by." />
      <meta name="keywords" content="корпоративное обучение, тренинги по продажам, обучение персонала, бизнес-тренинги" />
      <meta name="robots" content="noindex, follow" />
      <meta name="geo.region" content="BY-MI" />
      <meta name="geo.placename" content="Минск" />
      <meta name="geo.position" content="53.9006;27.5590" />
      <meta property="og:title" content="Страница не найдена — 404 | BusinessTraining.by" />
      <meta property="og:description" content="Запрашиваемая страница не найдена. Ознакомьтесь с корпоративными тренингами, обучением продажам, переговорам и другими программами BusinessTraining.by." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://business-training.by/" />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:image" content="https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/d5f5f47c7d94e3dc67c5ef073553230f.jpeg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Страница не найдена — 404 | BusinessTraining.by" />
      <meta name="twitter:description" content="Запрашиваемая страница не найдена. Ознакомьтесь с корпоративными тренингами, обучением продажам, переговорам и другими программами BusinessTraining.by." />
      <meta name="twitter:image" content="https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/d5f5f47c7d94e3dc67c5ef073553230f.jpeg" />
    </>
  );
}

export default function NotFound() {
  const location = useLocation();

  // Для 404 canonical НЕ устанавливаем (enabled=false)
  useCanonical(undefined, false);

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <PageMeta404 />

      <Navbar
        links={navLinks}
        ctaLabel="На главную"
        ctaTarget="home"
      />

      {/* 404 Content */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold text-teal-600 uppercase tracking-widest mb-4">
            Ошибка 404
          </p>
          <h1 className="text-6xl md:text-8xl font-black text-gray-900 mb-6">
            Страница не найдена
          </h1>
          <p className="text-lg md:text-xl text-gray-500 leading-relaxed mb-3 max-w-xl mx-auto">
            Запрашиваемая страница не существует или была перемещена.
          </p>
          <p className="text-sm text-gray-400 font-mono mb-10">
            {location.pathname}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              Вернуться на главную
            </Link>
            <a
              href="mailto:info@business-training.by"
              className="inline-flex items-center gap-2 text-gray-600 px-6 py-3 rounded-lg text-sm font-medium hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Написать нам
            </a>
          </div>
        </div>
      </section>

      {/* Useful Links Section */}
      <section className="py-16 md:py-20 px-6 bg-gray-50/70" id="encyclopedia">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Возможно, вы искали
            </h2>
            <p className="text-gray-500 text-base max-w-lg mx-auto">
              Ознакомьтесь с нашими программами корпоративного обучения — возможно, нужная информация находится здесь
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Энциклопедия */}
            <div>
              <h3 className="text-sm font-semibold text-teal-600 uppercase tracking-wider mb-5 flex items-center gap-2">
                <i className="ri-book-read-line"></i>
                Энциклопедия корпоративного обучения
              </h3>
              <div className="flex flex-col gap-1">
                {encyclopediaItems.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.to}
                    className="text-gray-600 hover:text-teal-700 hover:bg-white py-2.5 px-3 -mx-3 rounded-lg transition-colors text-sm cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-teal-400"></i>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Продажи и переговоры */}
            <div>
              <h3 className="text-sm font-semibold text-teal-600 uppercase tracking-wider mb-5 flex items-center gap-2">
                <i className="ri-line-chart-line"></i>
                Продажи и переговоры
              </h3>
              <div className="flex flex-col gap-1">
                {salesItems.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.to}
                    className="text-gray-600 hover:text-teal-700 hover:bg-white py-2.5 px-3 -mx-3 rounded-lg transition-colors text-sm cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-teal-400"></i>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal-600 font-medium text-sm hover:text-teal-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Вернуться на главную страницу
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}